import { PhotoTrigger } from '../components/photo-viewer.tsx';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi, fileApi, inspectionApi, type InspectionItemInput } from '../api/endpoints.ts';
import { API_BASE, ApiRequestError } from '../api/client.ts';
import {
  Alert,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  LoadingBlock,
  Select,
  TextArea,
  TextInput,
} from '../components/ui.tsx';
import { WorkPage, WorkSection } from '../components/work-page.tsx';
import { offlineAuthorizationFor, useSession } from '../state/session.tsx';
import { useDocumentTitle, useOnlineStatus, useUnsavedChangesWarning } from '../hooks/index.ts';
import {
  clearInspectionDraft,
  inspectionBaseAfterNotesSave,
  inspectionDraftFingerprint,
  inspectionMatchesDraft,
  loadInspectionDraft,
  saveInspectionDraft,
  type InspectionDraft,
  type InspectionDraftAcknowledgment,
  type InspectionDraftQueue,
  type InspectionDraftScope,
  type InspectionDraftToSave,
} from '../state/inspection-draft-store.ts';
import { formatShortDate } from '../i18n/datetime.ts';
import { INSPECTION_RATING_MAX, INSPECTION_RATING_MIN } from '@shared/constants/index.ts';
import type { Inspection, InspectionAction } from '@shared/types/index.ts';
import type { InspectionLineSeed } from '@shared/inspection-lines.ts';
import { formatScore } from './inspections.tsx';
import {
  chooseLineSource,
  InspectionLineSourceField,
  useInspectionLineSources,
  type InspectionLineSource,
} from '../components/inspection-line-source.tsx';

/** The lines as the form holds them, each with a key that survives a re-order. */
interface DraftItem extends InspectionItemInput {
  key: string;
}

let nextKey = 0;
const inspectionOwnerSource = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
let nextInspectionOwnerGeneration = 0;

function draftKey(): string {
  nextKey += 1;
  return `line-${nextKey}`;
}

function toDraft(inspection: Inspection): DraftItem[] {
  return inspection.items.map((item) => ({
    key: draftKey(),
    id: item.id,
    areaName: item.areaName,
    itemName: item.itemName,
    rating: item.rating,
    comment: item.comment,
    photoFileId: item.photoFileId,
  }));
}

function storedItems(items: readonly DraftItem[]): InspectionItemInput[] {
  return items.map(({ key: _key, ...item }) => ({
    ...item,
    rating: item.rating ?? null,
    comment: item.comment || null,
    photoFileId: item.photoFileId ?? null,
  }));
}

function draftItems(items: readonly InspectionItemInput[]): DraftItem[] {
  return items.map((item) => ({ ...item, key: draftKey() }));
}

class InspectionReplayConflict extends Error {
  constructor(
    readonly server: Inspection,
    readonly requestViewScope?: string,
  ) {
    super('This walkthrough changed on the server while this device had local work.');
    this.name = 'InspectionReplayConflict';
  }
}

class InspectionSyncFailure extends Error {
  constructor(
    readonly failure: unknown,
    readonly snapshot: InspectionDraftToSave,
    readonly requestScope: InspectionDraftScope,
    readonly requestViewScope: string,
  ) {
    super(failure instanceof Error ? failure.message : 'Could not sync this walkthrough.');
    this.name = 'InspectionSyncFailure';
  }
}

type InspectionConflict = 'none' | 'server-change' | 'completed-lines';

/**
 * One walkthrough: the lines, the score they add up to, and what was promised
 * about the bad ones.
 *
 * The page is deliberately a form rather than a reader. A supervisor walking a
 * building types into it, and the thing that makes an inspection worth having
 * is that the scoring and the corrective action happen in the same sitting —
 * a scorecard filed with nobody assigned to fix the 2 is the exact pattern
 * this feature exists to break.
 */
export default function InspectionDetailPage() {
  const { t } = useTranslation();
  const { inspectionId } = useParams<{ inspectionId: string }>();
  const queryClient = useQueryClient();
  const { can, user, company, currentLocation } = useSession();
  const isOnline = useOnlineStatus();
  // The nav lets a read-only member in on purpose; the forms must not.
  const canWrite = can('schedule:write');
  // The route may stay mounted while a user switches branches. Key both the
  // query and the local fallback to the active tenant scope so an inspection
  // from the branch just left cannot remain visible during a failed refetch.
  const userId = user?.id ?? null;
  const companyId = company?.id ?? null;
  const locationId = currentLocation?.id ?? null;
  const cacheAuthorization = user && company ? offlineAuthorizationFor(user, company) : null;
  const inspectionCacheScope = JSON.stringify([userId, companyId, locationId, cacheAuthorization]);
  /*
    Inspection drafts should move only when their own access boundary moves.
    The general API cache includes every permission, role and plan feature, but
    changing an unrelated permission (or upgrading a plan) must not make a
    half-scored walkthrough disappear. Read access and the account lock are the
    facts that can make restoring this particular data unsafe. Losing write
    permission disables replay below, but does not strand recovery data the
    same user may still read (or regain permission to finish later).
  */
  const draftAuthorization = JSON.stringify([can('schedule:read'), company?.accessLocked ?? false]);
  const inspectionAccessScope = JSON.stringify([userId, companyId, locationId, draftAuthorization]);
  const inspectionViewScope = JSON.stringify([inspectionId ?? null, inspectionAccessScope]);
  const ownerActivation = useRef<{ scope: string; ownerId: string } | null>(null);
  if (!ownerActivation.current || ownerActivation.current.scope !== inspectionViewScope) {
    nextInspectionOwnerGeneration += 1;
    ownerActivation.current = {
      scope: inspectionViewScope,
      ownerId: inspectionOwnerSource + ':' + nextInspectionOwnerGeneration,
    };
  }
  const inspectionEditOwnerId = ownerActivation.current.ownerId;
  const draftStorageScope = useMemo<InspectionDraftScope | null>(
    () =>
      userId && companyId
        ? {
            userId,
            companyId,
            locationId,
            authorization: draftAuthorization,
          }
        : null,
    [companyId, draftAuthorization, locationId, userId],
  );
  const inspectionQueryKey = useMemo(
    () => ['inspection', inspectionId, inspectionCacheScope] as const,
    [inspectionCacheScope, inspectionId],
  );

  const inspection = useQuery({
    queryKey: inspectionQueryKey,
    queryFn: ({ signal }) => inspectionApi.get(inspectionId as string, signal),
    enabled: Boolean(inspectionId),
  });

  /*
    Every photograph stored against this walkthrough, attached or not. The
    upload lands immediately and the line only holds the id until the next
    save, so a save that fails — or a phone that goes through a tunnel — would
    otherwise leave a photo paid for, invisible and unreachable. This is what
    makes it recoverable.
  */
  const photos = useQuery({
    queryKey: ['inspection-photos', inspectionId, inspectionCacheScope],
    queryFn: () => fileApi.listForInspection(inspectionId as string),
    enabled: Boolean(inspectionId),
  });

  const [storedInspection, setStoredInspection] = useState<{
    scope: string;
    inspection: Inspection;
  } | null>(null);
  const rememberInspection = useCallback(
    (next: Inspection) => setStoredInspection({ scope: inspectionAccessScope, inspection: next }),
    [inspectionAccessScope],
  );
  const [items, setItems] = useState<DraftItem[]>([]);
  const [internalNotes, setInternalNotes] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  /*
    Tracked apart because they are saved apart. One flag would have "save
    notes" claim the scored lines were saved too — and, worse, would drop the
    warning on the Complete dialog that says unsaved lines will not count.
  */
  const [linesDirty, setLinesDirty] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);
  const [confirmingComplete, setConfirmingComplete] = useState(false);
  // The conflict alert's only button deletes the ratings and notes kept on
  // this device, so it asks first, starting on Cancel.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  // Loading the server copy takes the conflict alert, and the button that
  // asked, off the page; focus goes to the score card instead of the body.
  const [focusAfterDiscard, setFocusAfterDiscard] = useState(false);
  const scoreCard = useRef<HTMLElement>(null);

  const [draftLookupComplete, setDraftLookupComplete] = useState(false);
  const [queuedMode, setQueuedMode] = useState<InspectionDraftQueue>(null);
  const [conflict, setConflict] = useState<InspectionConflict>('none');
  const [syncError, setSyncError] = useState<string | null>(null);
  const [storageError, setStorageError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [photoWrites, setPhotoWrites] = useState(0);
  const photoWritesRef = useRef(0);

  // A remembered server copy is a network-failure fallback, never permission
  // to keep showing a deleted or forbidden walkthrough after a definitive
  // live response. Unsynced local work remains visible so the user can recover
  // it, matching the quote editor's no-data-loss boundary.
  const definitiveInspectionError = inspection.error instanceof ApiRequestError;
  const hasDirtyInspectionRecovery = linesDirty || notesDirty;
  const data =
    definitiveInspectionError && !hasDirtyInspectionRecovery
      ? null
      : (inspection.data ?? (storedInspection?.scope === inspectionAccessScope ? storedInspection.inspection : null));
  useDocumentTitle(data?.siteName ?? t('inspections.detailTitle'));

  const itemsRef = useRef<DraftItem[]>([]);
  const internalNotesRef = useRef('');
  const customerNotesRef = useRef('');
  const linesDirtyRef = useRef(false);
  const notesDirtyRef = useRef(false);
  const lineRevision = useRef(0);
  const noteRevision = useRef(0);
  const draftRevision = useRef(0);
  const inheritedDraftAcknowledgment = useRef<InspectionDraftAcknowledgment | null>(null);
  const baseFingerprint = useRef<string | null>(null);
  const baseInspection = useRef<Inspection | null>(null);
  const queuedModeRef = useRef<InspectionDraftQueue>(null);
  const requiresPreflight = useRef(false);
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attempted = useRef<string | null>(null);
  const activeInspectionScope = useRef(inspectionViewScope);
  activeInspectionScope.current = inspectionViewScope;

  const adoptServer = useCallback(
    (server: Inspection) => {
      const nextItems = toDraft(server);
      itemsRef.current = nextItems;
      internalNotesRef.current = server.internalNotes ?? '';
      customerNotesRef.current = server.customerNotes ?? '';
      linesDirtyRef.current = false;
      notesDirtyRef.current = false;
      queuedModeRef.current = null;
      requiresPreflight.current = false;
      baseInspection.current = server;
      baseFingerprint.current = inspectionDraftFingerprint(server);
      setItems(nextItems);
      setInternalNotes(internalNotesRef.current);
      setCustomerNotes(customerNotesRef.current);
      setLinesDirty(false);
      setNotesDirty(false);
      setQueuedMode(null);
      rememberInspection(server);
      setConflict('none');
      setSyncError(null);
    },
    [rememberInspection],
  );

  const adoptDraft = useCallback(
    (draft: InspectionDraft) => {
      const nextItems = draftItems(draft.items);
      itemsRef.current = nextItems;
      internalNotesRef.current = draft.internalNotes;
      customerNotesRef.current = draft.customerNotes;
      linesDirtyRef.current = draft.linesDirty;
      notesDirtyRef.current = draft.notesDirty;
      queuedModeRef.current = draft.queued;
      // Any work recovered after the page lifetime that created it must prove
      // the server has not moved, even when the stored flag predates this load.
      requiresPreflight.current = true;
      baseInspection.current = draft.inspection;
      baseFingerprint.current = draft.baseFingerprint;
      draftRevision.current = draft.revision;
      inheritedDraftAcknowledgment.current = {
        ...(draft.editorOwnerId ? { editorOwnerId: draft.editorOwnerId } : {}),
        key: draft.key,
        throughRevision: draft.revision,
      };
      setItems(nextItems);
      setInternalNotes(draft.internalNotes);
      setCustomerNotes(draft.customerNotes);
      setLinesDirty(draft.linesDirty);
      setNotesDirty(draft.notesDirty);
      setQueuedMode(draft.queued);
      rememberInspection(draft.inspection);
    },
    [rememberInspection],
  );

  const currentDraft = useCallback(
    (queue = queuedModeRef.current): InspectionDraftToSave | null => {
      const inspectionSnapshot = baseInspection.current;
      const fingerprint = baseFingerprint.current;
      if (!inspectionId || !inspectionSnapshot || !fingerprint) return null;
      return {
        inspectionId,
        inspection: inspectionSnapshot,
        baseFingerprint: fingerprint,
        items: storedItems(itemsRef.current),
        internalNotes: internalNotesRef.current,
        customerNotes: customerNotesRef.current,
        linesDirty: linesDirtyRef.current,
        notesDirty: notesDirtyRef.current,
        queued: queue,
        requiresPreflight: requiresPreflight.current,
        editorOwnerId: inspectionEditOwnerId,
        revision: draftRevision.current,
      };
    },
    [inspectionEditOwnerId, inspectionId],
  );

  const acknowledgmentsFor = useCallback((snapshot: InspectionDraftToSave): InspectionDraftAcknowledgment[] => {
    const acknowledgments: InspectionDraftAcknowledgment[] = [
      {
        ...(snapshot.editorOwnerId ? { editorOwnerId: snapshot.editorOwnerId } : {}),
        throughRevision: snapshot.revision,
      },
    ];
    const inherited = inheritedDraftAcknowledgment.current;
    if (
      inherited &&
      (!inherited.editorOwnerId || inherited.editorOwnerId !== snapshot.editorOwnerId || inherited.key)
    ) {
      acknowledgments.push({ ...inherited });
    }
    return acknowledgments;
  }, []);

  const persistCurrent = useCallback(async () => {
    if (!linesDirtyRef.current && !notesDirtyRef.current) return 'saved' as const;
    const draft = currentDraft();
    if (!draft) return 'inactive' as const;
    const result = draftStorageScope ? await saveInspectionDraft(draft, draftStorageScope) : 'inactive';
    if (result === 'entry-too-large' || result === 'capacity-reached') {
      setStorageError(
        result === 'entry-too-large'
          ? 'This walkthrough is too large for protected offline storage. Save it while online before leaving.'
          : 'Offline inspection storage is full. Sync another walkthrough before starting more offline work.',
      );
    } else if (result === 'inactive') {
      setStorageError(
        'This browser could not protect the walkthrough on this device. Reconnect and save before closing this page.',
      );
    } else {
      setStorageError(null);
    }
    return result;
  }, [currentDraft, draftStorageScope]);

  const schedulePersist = useCallback(() => {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      persistTimer.current = null;
      void persistCurrent();
    }, 250);
  }, [persistCurrent]);

  useEffect(() => {
    let canceled = false;
    itemsRef.current = [];
    internalNotesRef.current = '';
    customerNotesRef.current = '';
    linesDirtyRef.current = false;
    notesDirtyRef.current = false;
    lineRevision.current = 0;
    noteRevision.current = 0;
    draftRevision.current = 0;
    inheritedDraftAcknowledgment.current = null;
    baseFingerprint.current = null;
    baseInspection.current = null;
    queuedModeRef.current = null;
    requiresPreflight.current = false;
    setItems([]);
    setInternalNotes('');
    setCustomerNotes('');
    setLinesDirty(false);
    setNotesDirty(false);
    setQueuedMode(null);
    setDraftLookupComplete(false);
    setStoredInspection(null);
    setConflict('none');
    setSyncError(null);
    setStorageError(null);
    setConfirmingComplete(false);
    setConfirmingDiscard(false);
    attempted.current = null;
    void loadInspectionDraft(inspectionId as string, draftStorageScope ?? undefined).then((draft) => {
      if (canceled) return;
      if (draft) adoptDraft(draft);
      setDraftLookupComplete(true);
    });
    return () => {
      canceled = true;
      if (persistTimer.current) {
        clearTimeout(persistTimer.current);
        persistTimer.current = null;
      }
      // The session may already have activated the next location by the time
      // this passive cleanup runs. Snapshot the outgoing refs now and write
      // them with the scope this render captured, never whichever scope is
      // globally active after the switch.
      if ((linesDirtyRef.current || notesDirtyRef.current) && draftStorageScope) {
        const outgoing = currentDraft();
        if (outgoing) void saveInspectionDraft(outgoing, draftStorageScope);
      }
    };
  }, [adoptDraft, currentDraft, draftStorageScope, inspectionId, inspectionViewScope]);

  // A local draft wins the form, but never silently wins the server. A fresh
  // query that differs from the base turns into a visible, durable conflict.
  useEffect(() => {
    const server = inspection.data;
    if (!draftLookupComplete || !server) return;
    if (linesDirtyRef.current || notesDirtyRef.current) {
      if (baseFingerprint.current && inspectionDraftFingerprint(server) !== baseFingerprint.current) {
        rememberInspection(server);
        setConflict(
          linesDirtyRef.current && server.status === 'completed' && baseInspection.current?.status !== 'completed'
            ? 'completed-lines'
            : 'server-change',
        );
        setSyncError('This walkthrough changed elsewhere. Automatic sync stopped; your local draft is still safe.');
      }
      return;
    }
    adoptServer(server);
  }, [adoptServer, draftLookupComplete, inspection.data, rememberInspection]);

  useUnsavedChangesWarning(linesDirty || notesDirty);

  const completed = data?.status === 'completed';
  const canceled = data?.status === 'cancelled';
  // Frozen once complete: the score was snapshotted from these lines and the
  // client may already be looking at that number.
  const linesEditable = Boolean(data) && !completed && !canceled;

  const sync = useMutation({
    mutationFn: async (mode: Exclude<InspectionDraftQueue, null>) => {
      const snapshot = currentDraft(mode);
      if (!snapshot) throw new Error('The inspection draft is not ready yet.');
      const requestScope = draftStorageScope;
      const requestViewScope = inspectionViewScope;
      if (!requestScope) throw new Error('The inspection draft is not ready yet.');
      const requestAcknowledgments = acknowledgmentsFor(snapshot);
      const sentItems = [...itemsRef.current];
      const sentLineRevision = lineRevision.current;
      const sentNoteRevision = noteRevision.current;
      try {
        const saved = await saveInspectionDraft(snapshot, requestScope);
        if (saved !== 'saved') {
          // Durable local storage is a safety net, not a prerequisite for a
          // live save. Keep the warning visible, then let the network-only
          // preflight and atomic PATCH proceed. If they fail, the form remains
          // dirty and the warning truthfully tells the user not to leave.
          if (activeInspectionScope.current === requestViewScope) {
            setStorageError(
              saved === 'inactive'
                ? 'This browser could not protect the walkthrough on this device. Reconnect before leaving this page.'
                : saved === 'entry-too-large'
                  ? 'This walkthrough is too large for protected offline storage. Save it while online before leaving.'
                  : 'Offline inspection storage is full. Sync another walkthrough before starting more offline work.',
            );
          }
        }

        let acknowledged: Inspection | null = null;

        // Inspections use required optimistic concurrency now. The fresh,
        // network-only read both detects an ambiguous previous ACK and supplies
        // the exact version the atomic PATCH must compare inside its transaction.
        const fresh = await inspectionApi.getFresh(inspectionId as string);
        const unchanged =
          mode === 'notes'
            ? (fresh.internalNotes ?? '') === (snapshot.inspection.internalNotes ?? '') &&
              (fresh.customerNotes ?? '') === (snapshot.inspection.customerNotes ?? '')
            : inspectionDraftFingerprint(fresh) === snapshot.baseFingerprint;
        if (!unchanged) {
          // The request may have committed and lost only its response. An exact
          // match is the missing ACK, not somebody else's edit.
          if (inspectionMatchesDraft(fresh, snapshot, mode)) acknowledged = fresh;
          else throw new InspectionReplayConflict(fresh);
        }

        const freshLinesEditable = fresh.status !== 'completed' && fresh.status !== 'cancelled';
        const updated =
          acknowledged ??
          (await inspectionApi.update(inspectionId as string, {
            version: fresh.version,
            internalNotes: snapshot.internalNotes || null,
            customerNotes: snapshot.customerNotes || null,
            ...(mode === 'all' && freshLinesEditable ? { items: snapshot.items } : {}),
          }));
        return {
          updated,
          mode,
          snapshot,
          requestScope,
          requestViewScope,
          requestAcknowledgments,
          sentItems,
          sentLineRevision,
          sentNoteRevision,
        };
      } catch (error) {
        throw new InspectionSyncFailure(error, snapshot, requestScope, requestViewScope);
      }
    },
    onSuccess: ({
      updated,
      mode,
      snapshot,
      requestScope,
      requestViewScope,
      requestAcknowledgments,
      sentItems,
      sentLineRevision,
      sentNoteRevision,
    }) => {
      if (activeInspectionScope.current !== requestViewScope) {
        // The ACK belongs to the branch the request left from. It may retire
        // only the revision it sent; cleanup may already have stored a newer
        // old-branch edit. A notes-only response did not acknowledge dirty
        // lines, so leave that exact recovery intact for its next preflight.
        if (mode !== 'notes' || !snapshot.linesDirty) {
          void clearInspectionDraft(snapshot.inspectionId, requestScope, requestAcknowledgments);
        }
        return;
      }

      const linesUnchanged = lineRevision.current === sentLineRevision;
      const notesUnchanged = noteRevision.current === sentNoteRevision;

      if (mode === 'all') {
        if (linesUnchanged) {
          const next = toDraft(updated);
          itemsRef.current = next;
          setItems(next);
          linesDirtyRef.current = false;
          setLinesDirty(false);
        } else {
          // Preserve edits made in flight while adopting ids the acknowledged
          // declarative replacement assigned to new rows.
          const idByKey = new Map(sentItems.map((item, index) => [item.key, updated.items[index]?.id] as const));
          const next = itemsRef.current.map((item) => ({
            ...item,
            id: item.id ?? idByKey.get(item.key),
          }));
          itemsRef.current = next;
          setItems(next);
        }
      }

      if (notesUnchanged) {
        internalNotesRef.current = updated.internalNotes ?? '';
        customerNotesRef.current = updated.customerNotes ?? '';
        setInternalNotes(internalNotesRef.current);
        setCustomerNotes(customerNotesRef.current);
        notesDirtyRef.current = false;
        setNotesDirty(false);
      }

      if (mode === 'notes' && linesDirtyRef.current) {
        // A notes-only PATCH never acknowledges this device's dirty lines.
        // Keep the exact status/items those edits were based on while advancing
        // the note fields the server did accept. In particular, `updated` may
        // contain a colleague's newer scores; adopting those as the line base
        // would let the next Save lines overwrite that intervening work.
        const preservedBase = inspectionBaseAfterNotesSave(snapshot.inspection, updated);
        baseInspection.current = preservedBase;
        baseFingerprint.current = inspectionDraftFingerprint(preservedBase);
      } else {
        baseInspection.current = updated;
        baseFingerprint.current = inspectionDraftFingerprint(updated);
      }
      // A notes-only preflight intentionally ignores scores, but its fresh
      // response can reveal that another supervisor froze them while this
      // browser still has unsaved line work. Derive the conflict from the ACK,
      // not the status captured before the request left.
      const frozenLineConflict: InspectionConflict =
        mode === 'notes' && linesDirtyRef.current
          ? updated.status === 'completed'
            ? 'completed-lines'
            : updated.status === 'cancelled'
              ? 'server-change'
              : 'none'
          : 'none';

      // An edit made while this request traveled was not covered by its
      // preflight. Keep the proof requirement for the automatically queued
      // follow-up; otherwise the first ACK would turn that second write into a
      // blind versionless PATCH.
      requiresPreflight.current = frozenLineConflict !== 'none' || !linesUnchanged || !notesUnchanged;
      rememberInspection(updated);
      setConflict(frozenLineConflict);
      if (frozenLineConflict === 'none') setSyncError(null);
      queryClient.setQueryData(inspectionQueryKey, updated);
      void queryClient.invalidateQueries({ queryKey: ['inspections'] });

      const nextQueue: InspectionDraftQueue =
        mode === 'all'
          ? linesDirtyRef.current || notesDirtyRef.current
            ? 'all'
            : null
          : notesDirtyRef.current
            ? 'notes'
            : null;
      queuedModeRef.current = nextQueue;
      setQueuedMode(nextQueue);
      attempted.current = null;

      if (!linesDirtyRef.current && !notesDirtyRef.current) {
        // A server ACK is durable even when IndexedDB was unavailable. The
        // local-storage warning has served its purpose and may now clear.
        setStorageError(null);
        void clearInspectionDraft(snapshot.inspectionId, requestScope, requestAcknowledgments);
      } else {
        draftRevision.current += 1;
        // Make the follow-up owner revision durable before retiring the sent
        // revision. Whichever IndexedDB transaction runs first, the clear CAS
        // keeps the newer same-owner row; waiting also closes the crash window
        // between deleting the old recovery and storing the unsent remainder.
        void persistCurrent().then((result) => {
          if (result === 'saved') {
            void clearInspectionDraft(snapshot.inspectionId, requestScope, requestAcknowledgments);
          }
        });
        if (nextQueue) setRetryNonce((value) => value + 1);
      }
    },
    onError: (error) => {
      if (error instanceof InspectionSyncFailure && activeInspectionScope.current !== error.requestViewScope) {
        // Preserve the exact outgoing request under its original branch. The
        // store's revision guard refuses it if scope cleanup already wrote a
        // newer edit. The currently visible branch remains untouched.
        void saveInspectionDraft({ ...error.snapshot, requiresPreflight: true }, error.requestScope);
        return;
      }

      const failure = error instanceof InspectionSyncFailure ? error.failure : error;
      requiresPreflight.current = true;
      if (failure instanceof InspectionReplayConflict) {
        rememberInspection(failure.server);
        queryClient.setQueryData(inspectionQueryKey, failure.server);
        setConflict(
          linesDirtyRef.current &&
            failure.server.status === 'completed' &&
            baseInspection.current?.status !== 'completed'
            ? 'completed-lines'
            : 'server-change',
        );
        setSyncError('This walkthrough changed elsewhere. Automatic sync stopped; your local draft is still safe.');
      } else {
        setSyncError(
          failure instanceof Error
            ? failure.message
            : 'Could not sync this walkthrough. The local draft is still safe.',
        );
      }
      draftRevision.current += 1;
      void persistCurrent();
    },
  });

  function requestSync(mode: Exclude<InspectionDraftQueue, null>) {
    if (conflict !== 'none' && !(conflict === 'completed-lines' && mode === 'notes')) {
      setSyncError('Review the server change before retrying. Your local draft has not been removed.');
      return;
    }
    const nextMode: Exclude<InspectionDraftQueue, null> =
      conflict === 'completed-lines' && mode === 'notes' ? 'notes' : queuedModeRef.current === 'all' ? 'all' : mode;
    queuedModeRef.current = nextMode;
    // Every save proves its base with a network-only GET, then sends that
    // exact version into the Worker's atomic compare-and-swap transaction.
    requiresPreflight.current = true;
    draftRevision.current += 1;
    setQueuedMode(nextMode);
    // The queue marker must reach IndexedDB before an offline tab can close;
    // waiting for the reconnect effect would protect the form but forget that
    // the user had already asked to send it.
    void persistCurrent();
    attempted.current = null;
    setRetryNonce((value) => value + 1);
  }

  // A queued button press is attempted once now, and once again on the next
  // online transition. Failures do not form a busy retry loop; the durable row
  // waits for an actual reconnect or another explicit press.
  useEffect(() => {
    if (!isOnline) {
      attempted.current = null;
      return;
    }
    if (
      !draftLookupComplete ||
      !canWrite ||
      (conflict !== 'none' && !(conflict === 'completed-lines' && queuedMode === 'notes')) ||
      !queuedMode ||
      sync.isPending
    )
      return;
    const stamp = `${draftRevision.current}:${queuedMode}:${retryNonce}`;
    if (attempted.current === stamp) return;
    attempted.current = stamp;
    sync.mutate(queuedMode);
    // `sync` is a mutation observer and changes identity with its state. The
    // explicit stamp above is the retry boundary; depending on the observer
    // would turn a failed request into an immediate infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canWrite, conflict, draftLookupComplete, isOnline, queuedMode, retryNonce]);

  const discardLocal = useMutation({
    mutationFn: async () => {
      const requestScope = draftStorageScope;
      if (!requestScope) throw new Error('The inspection draft is not ready yet.');
      const requestViewScope = inspectionViewScope;
      const requestRevision = draftRevision.current;
      const requestDraft = currentDraft();
      const requestAcknowledgments = requestDraft ? acknowledgmentsFor(requestDraft) : [];
      const fresh = await inspectionApi.getFresh(inspectionId as string);
      return { fresh, requestScope, requestViewScope, requestRevision, requestAcknowledgments };
    },
    onSuccess: async ({ fresh, requestScope, requestViewScope, requestRevision, requestAcknowledgments }) => {
      // This is the sole non-ACK deletion path inside the page, and it is
      // deliberately an explicit conflict-resolution choice by the user.
      // If the user typed again while the network-only read traveled, that
      // newer work was not part of the discard they initiated. Leave both the
      // form and its durable row alone instead of adopting the response over
      // edits made after the click.
      if (activeInspectionScope.current === requestViewScope && draftRevision.current !== requestRevision) {
        setSyncError('The walkthrough changed again while loading the server copy. The newer local edits were kept.');
        setConfirmingDiscard(false);
        return;
      }
      await clearInspectionDraft(fresh.id, requestScope, requestAcknowledgments);
      // Another walkthrough is open now. Its own question and focus are not
      // this request's to close or move.
      if (activeInspectionScope.current !== requestViewScope) return;
      adoptServer(fresh);
      queryClient.setQueryData(inspectionQueryKey, fresh);
      setConfirmingDiscard(false);
      setFocusAfterDiscard(true);
    },
  });

  useEffect(() => {
    if (!focusAfterDiscard) return;
    setFocusAfterDiscard(false);
    // Still in conflict (newer local edits were kept): the opener is still
    // there, and the dialog hands focus back to it.
    if (conflict === 'none') scoreCard.current?.focus({ preventScroll: true });
  }, [conflict, focusAfterDiscard]);

  const complete = useMutation({
    mutationFn: async () => {
      if (!isOnline) throw new Error('Reconnect before completing this walkthrough.');
      if (photoWritesRef.current > 0) {
        throw new Error('Wait for the photograph change to finish before completing this walkthrough.');
      }
      const requestScope = draftStorageScope;
      if (!requestScope) throw new Error('The inspection draft is not ready yet.');
      const requestViewScope = inspectionViewScope;
      const snapshot = currentDraft();
      const requestAcknowledgments = snapshot ? acknowledgmentsFor(snapshot) : [];
      const updated = await inspectionApi.complete(inspectionId as string);
      return { updated, requestScope, requestViewScope, snapshot, requestAcknowledgments };
    },
    onSuccess: ({ updated, requestScope, requestViewScope, snapshot, requestAcknowledgments }) => {
      if (activeInspectionScope.current !== requestViewScope) {
        if (snapshot && (snapshot.linesDirty || snapshot.notesDirty)) {
          // Dirty scores must keep their pre-completion base so reopening sees
          // the completed status as a deterministic conflict; completed lines
          // can never be replayed. Note-only work remains safely rebaseable on
          // the completed response because that endpoint still accepts notes.
          const preservedBase = snapshot.linesDirty
            ? snapshot.inspection
            : {
                ...updated,
                // Completion acknowledges the status transition, not this
                // device's dirty notes. Retain the note values the edit was
                // based on so a later replay detects another supervisor's
                // intervening note change instead of overwriting it.
                internalNotes: snapshot.inspection.internalNotes,
                customerNotes: snapshot.inspection.customerNotes,
              };
          void saveInspectionDraft(
            {
              ...snapshot,
              inspection: preservedBase,
              baseFingerprint: snapshot.linesDirty
                ? snapshot.baseFingerprint
                : inspectionDraftFingerprint(preservedBase),
              queued: null,
              requiresPreflight: true,
            },
            requestScope,
          );
        } else {
          void clearInspectionDraft(updated.id, requestScope, requestAcknowledgments);
        }
        return;
      }

      // Capture current refs before adopting the completed response. A user
      // can keep typing while completion travels; if any score remains dirty,
      // its durable base must stay pre-completion so a reload deterministically
      // reports that frozen work as a conflict instead of treating it as an
      // ordinary draft against the already-completed server state.
      const unresolvedAtCompletion = currentDraft(null);
      if (persistTimer.current) {
        clearTimeout(persistTimer.current);
        persistTimer.current = null;
      }

      setConfirmingComplete(false);
      queryClient.setQueryData(inspectionQueryKey, updated);
      /*
        The lines are adopted from the server — they are frozen now, and the
        form must show what was actually scored. A note the supervisor has
        typed and not yet saved is NOT overwritten: notes stay editable after
        completion, the dialog warns only about lines, and quietly reverting
        somebody's half-written note to the client because they pressed
        Complete would be a loss they never agreed to.
      */
      if (!linesDirtyRef.current) {
        const completedItems = toDraft(updated);
        setItems(completedItems);
        itemsRef.current = completedItems;
        setLinesDirty(false);
        linesDirtyRef.current = false;
      }
      if (!notesDirtyRef.current) {
        setInternalNotes(updated.internalNotes ?? '');
        setCustomerNotes(updated.customerNotes ?? '');
        internalNotesRef.current = updated.internalNotes ?? '';
        customerNotesRef.current = updated.customerNotes ?? '';
      }
      if (!linesDirtyRef.current) {
        const priorBase = unresolvedAtCompletion?.inspection ?? snapshot?.inspection ?? baseInspection.current;
        const nextBase = notesDirtyRef.current
          ? {
              ...updated,
              // Keep the pre-completion note base. Saving notes performs a
              // fresh comparison against these two values; rebasing them to
              // the completion response would hide an intervening edit.
              internalNotes: priorBase?.internalNotes ?? null,
              customerNotes: priorBase?.customerNotes ?? null,
            }
          : updated;
        baseInspection.current = nextBase;
        baseFingerprint.current = inspectionDraftFingerprint(nextBase);
      }
      rememberInspection(updated);
      queuedModeRef.current = null;
      setQueuedMode(null);
      if (linesDirtyRef.current) {
        // Completion acknowledged the status transition, not the unsent
        // ratings. Those lines are now frozen, so preserve them locally and
        // make the unresolvable difference explicit instead of treating
        // completion as their ACK.
        setConflict('completed-lines');
        setSyncError('The walkthrough was completed before these local changes were saved. The local draft is intact.');
        requiresPreflight.current = true;
        draftRevision.current += 1;
        if (unresolvedAtCompletion) {
          void saveInspectionDraft(
            {
              ...unresolvedAtCompletion,
              queued: null,
              requiresPreflight: true,
              revision: draftRevision.current,
            },
            requestScope,
          );
        }
      } else if (notesDirtyRef.current) {
        // Notes remain editable after completion. Keep the local text as an
        // ordinary draft so the user can still press Save notes; a conflict
        // here would offer only discard even though the server accepts it.
        setConflict('none');
        setSyncError(null);
        requiresPreflight.current = true;
        draftRevision.current += 1;
        void persistCurrent();
      } else {
        void clearInspectionDraft(updated.id, requestScope, requestAcknowledgments);
      }
      void queryClient.invalidateQueries({ queryKey: ['inspections'] });
    },
  });

  const share = useMutation({
    mutationFn: async (sharedWithClient: boolean) => {
      const requestViewScope = inspectionViewScope;
      const dirtySnapshot = linesDirtyRef.current || notesDirtyRef.current ? currentDraft() : null;
      const fresh = await inspectionApi.getFresh(inspectionId as string);
      // Sharing itself is independent, but its response must not silently
      // become the base for dirty notes/ratings after the fresh read revealed
      // that one of those editable fields changed elsewhere.
      if (dirtySnapshot && inspectionDraftFingerprint(fresh) !== dirtySnapshot.baseFingerprint) {
        throw new InspectionReplayConflict(fresh, requestViewScope);
      }
      const updated = await inspectionApi.update(inspectionId as string, {
        version: fresh.version,
        sharedWithClient,
      });
      return { updated, requestViewScope };
    },
    onSuccess: ({ updated, requestViewScope }) => {
      if (activeInspectionScope.current !== requestViewScope) return;
      baseInspection.current = updated;
      rememberInspection(updated);
      queryClient.setQueryData(inspectionQueryKey, updated);
      if (linesDirtyRef.current || notesDirtyRef.current) void persistCurrent();
      void queryClient.invalidateQueries({ queryKey: ['inspections'] });
    },
    onError: (error) => {
      if (!(error instanceof InspectionReplayConflict)) return;
      if (error.requestViewScope && activeInspectionScope.current !== error.requestViewScope) return;
      rememberInspection(error.server);
      queryClient.setQueryData(inspectionQueryKey, error.server);
      setConflict(
        linesDirtyRef.current && error.server.status === 'completed' && baseInspection.current?.status !== 'completed'
          ? 'completed-lines'
          : 'server-change',
      );
      setSyncError('This walkthrough changed elsewhere. Automatic sync stopped; your local draft is still safe.');
      draftRevision.current += 1;
      void persistCurrent();
    },
  });

  /*
    A write is in flight. Completion must not overlap one: the score is
    snapshotted from the STORED lines, so completing while a save is still
    traveling would freeze a number computed from the lines it is replacing.
    A photograph counts — it is evidence being added to or removed from the
    walkthrough about to be frozen. Neither operation may race completion,
    because its callback would otherwise dirty an already-frozen form.
  */
  const writing = sync.isPending || photoWrites > 0;

  function editItem(key: string, patch: Partial<DraftItem>) {
    setItems((current) => {
      const next = current.map((item) => (item.key === key ? { ...item, ...patch } : item));
      itemsRef.current = next;
      return next;
    });
    setLinesDirty(true);
    linesDirtyRef.current = true;
    lineRevision.current += 1;
    draftRevision.current += 1;
    requiresPreflight.current = true;
    schedulePersist();
  }

  function addItem() {
    /*
      The new line inherits the previous line's area. A supervisor scores six
      things in one corridor before moving on, and retyping "Second floor
      east" six times is how areas end up spelled three ways in one document.
    */
    setItems((current) => {
      const next = [
        ...current,
        {
          key: draftKey(),
          areaName: current.at(-1)?.areaName ?? '',
          itemName: '',
          rating: null,
          comment: '',
          photoFileId: null,
        },
      ];
      itemsRef.current = next;
      return next;
    });
    setLinesDirty(true);
    linesDirtyRef.current = true;
    lineRevision.current += 1;
    draftRevision.current += 1;
    requiresPreflight.current = true;
    schedulePersist();
  }

  /** A whole list of checks at once, unscored, after whatever is already there. */
  function addItems(lines: readonly InspectionLineSeed[]) {
    if (lines.length === 0) return;
    setItems((current) => {
      const next = [
        ...current,
        ...lines.map((line) => ({
          key: draftKey(),
          areaName: line.areaName,
          itemName: line.itemName,
          rating: null,
          comment: '',
          photoFileId: null,
        })),
      ];
      itemsRef.current = next;
      return next;
    });
    setLinesDirty(true);
    linesDirtyRef.current = true;
    lineRevision.current += 1;
    draftRevision.current += 1;
    requiresPreflight.current = true;
    schedulePersist();
  }

  function removeItem(key: string) {
    setItems((current) => {
      const next = current.filter((item) => item.key !== key);
      itemsRef.current = next;
      return next;
    });
    setLinesDirty(true);
    linesDirtyRef.current = true;
    lineRevision.current += 1;
    draftRevision.current += 1;
    requiresPreflight.current = true;
    schedulePersist();
  }

  /**
   * What the score would be if it were snapshotted now.
   *
   * Computed here only to show the supervisor where they are — the number that
   * counts is the server's, from the stored lines, at completion. Unscored
   * lines are excluded from both halves, exactly as the server does it.
   */
  const provisional = useMemo(() => {
    const scored = items.filter((item) => typeof item.rating === 'number');
    if (scored.length === 0) return null;
    const total = scored.reduce((sum, item) => sum + (item.rating as number), 0);
    return Math.round((total * 10_000) / (scored.length * INSPECTION_RATING_MAX));
  }, [items]);

  if (!draftLookupComplete || (!data && inspection.isLoading)) {
    return (
      <WorkPage
        title={t('inspections.detailTitle')}
        actions={
          <Link to="/inspections">
            <Button>{t('inspections.backToList')}</Button>
          </Link>
        }
      >
        <WorkSection title={t('inspections.detailTitle')}>
          <LoadingBlock label={t('inspections.loadingOne')} />
        </WorkSection>
      </WorkPage>
    );
  }
  if (inspection.isError && !data) {
    return (
      <WorkPage
        title={t('inspections.detailTitle')}
        actions={
          <Link to="/inspections">
            <Button>{t('inspections.backToList')}</Button>
          </Link>
        }
      >
        <WorkSection title={t('inspections.detailTitle')}>
          <Alert tone="error" title={t('inspections.loadOneErrorTitle')}>
            {(inspection.error as Error).message}
          </Alert>
        </WorkSection>
      </WorkPage>
    );
  }
  if (!data) return null;

  return (
    <WorkPage
      title={data.siteName ?? t('inspections.unnamedSite')}
      description={t('inspections.detailDescription', {
        customer: data.customerName ?? t('inspections.unnamedCustomer'),
        date: formatShortDate(data.scheduledFor),
        status: t(`inspections.status.${data.status}`),
      })}
      actions={
        <>
          <Link to="/inspections">
            <Button>{t('inspections.backToList')}</Button>
          </Link>
          {linesEditable && canWrite ? (
            <Button
              variant="primary"
              busy={sync.isPending && sync.variables === 'all'}
              disabled={conflict !== 'none' || complete.isPending}
              onClick={() => requestSync('all')}
            >
              {t('inspections.save')}
            </Button>
          ) : null}
        </>
      }
    >
      {conflict !== 'none' ? (
        <Alert
          tone="warning"
          title={
            conflict === 'completed-lines' ? 'Unsaved ratings were not completed' : 'This walkthrough changed elsewhere'
          }
        >
          <p>
            {conflict === 'completed-lines'
              ? 'The saved server ratings were completed, so these local rating changes stay protected but cannot be applied. Notes can still be saved before you discard the local ratings.'
              : 'Automatic sync is paused. Your ratings and notes remain on this device; no server data was overwritten.'}
          </p>
          <div className="mt-2">
            <Button busy={discardLocal.isPending} disabled={!isOnline} onClick={() => setConfirmingDiscard(true)}>
              Discard local draft and load server copy
            </Button>
          </div>
          {confirmingDiscard ? (
            <ConfirmDialog
              title={t('inspections.discardConfirmTitle')}
              confirmLabel={t('inspections.discardConfirmAction')}
              confirmVariant="danger"
              initialFocus="cancel"
              confirmDisabled={!isOnline}
              busy={discardLocal.isPending}
              error={discardLocal.isError ? (discardLocal.error as Error).message : null}
              onCancel={() => setConfirmingDiscard(false)}
              onConfirm={() => discardLocal.mutate()}
            >
              <p>{t('inspections.discardConfirmBody')}</p>
              {conflict === 'completed-lines' && notesDirty ? (
                <p className="mt-2">{t('inspections.discardConfirmNotesFirst')}</p>
              ) : null}
            </ConfirmDialog>
          ) : null}
          {discardLocal.isError ? <p>{(discardLocal.error as Error).message}</p> : null}
        </Alert>
      ) : queuedMode ? (
        <Alert tone="warning" title={isOnline ? 'Syncing saved local work' : 'Saved on this device'}>
          {isOnline
            ? 'The local inspection draft is waiting for the server to acknowledge it.'
            : 'Keep this browser signed in. The draft will retry automatically when this page reconnects.'}
        </Alert>
      ) : !isOnline ? (
        <Alert tone="warning" title="Offline inspection mode">
          Ratings and notes are protected locally. Completion, sharing, corrective actions, and photographs need a
          connection.
        </Alert>
      ) : null}

      {storageError ? (
        <Alert tone="error" title="Offline protection unavailable">
          {storageError}
        </Alert>
      ) : null}

      <section ref={scoreCard} tabIndex={-1} className="card outline-none">
        <div className="flex flex-wrap items-baseline justify-between gap-4">
          <div>
            <p className="text-sm text-[color:var(--color-muted)]">
              {completed ? t('inspections.finalScore') : t('inspections.provisionalScore')}
            </p>
            <p className="text-3xl font-bold">
              {formatScore(completed ? data.scoreBasisPoints : provisional) ?? t('inspections.noScore')}
            </p>
          </div>
          <p className="text-sm text-[color:var(--color-muted)]">
            {t('inspections.scoredCount', {
              count: completed ? data.scoredItems : items.filter((item) => typeof item.rating === 'number').length,
              total: items.length,
            })}
          </p>
          {!completed && !canceled && canWrite ? (
            <Button
              variant="primary"
              disabled={writing || !isOnline || conflict !== 'none' || queuedMode !== null}
              onClick={() => setConfirmingComplete(true)}
            >
              {t('inspections.complete')}
            </Button>
          ) : null}
        </div>

        {completed ? (
          <p className="mt-3 text-sm text-[color:var(--color-muted)]">{t('inspections.frozenNotice')}</p>
        ) : null}
        {/*
          Said before the walk, not after: an area nobody could get into is
          left unscored, and an inspector who does not know that scores it a 1
          and punishes the crew for a locked door.
        */}
        {linesEditable ? (
          <p className="mt-3 text-sm text-[color:var(--color-muted)]">{t('inspections.notScoredHint')}</p>
        ) : null}
      </section>

      <ScoredLines
        inspectionId={data.id}
        propertyId={data.propertyId}
        scheduledFor={data.scheduledFor}
        items={items}
        editable={linesEditable && canWrite && !complete.isPending}
        uploadsOnline={isOnline}
        onEdit={editItem}
        onAdd={addItem}
        onAddMany={addItems}
        onRemove={removeItem}
        onUploadStateChange={(delta) => {
          photoWritesRef.current = Math.max(0, photoWritesRef.current + delta);
          setPhotoWrites(photoWritesRef.current);
        }}
      />

      {/*
        Photographs uploaded against this walkthrough that no line is showing.
        Ordinarily empty — a photo is attached by the save that follows it —
        so this appears exactly when something went wrong, which is the moment
        it is needed.
      */}
      <UnattachedPhotos
        photos={photos.data?.items ?? []}
        items={items}
        editable={linesEditable && canWrite}
        onAttach={(fileId, key) => editItem(key, { photoFileId: fileId })}
      />

      <section className="card mt-6">
        <h2 className="mb-4 text-lg font-semibold">{t('inspections.notesTitle')}</h2>
        <TextArea
          label={t('inspections.internalNotes')}
          hint={t('inspections.internalNotesHint')}
          tone="internal"
          rows={3}
          disabled={!canWrite}
          value={internalNotes}
          onChange={(event) => {
            const next = event.target.value;
            internalNotesRef.current = next;
            setInternalNotes(next);
            notesDirtyRef.current = true;
            setNotesDirty(true);
            noteRevision.current += 1;
            draftRevision.current += 1;
            requiresPreflight.current = true;
            schedulePersist();
          }}
        />
        <TextArea
          label={t('inspections.customerNotes')}
          hint={t('inspections.customerNotesHint')}
          rows={3}
          disabled={!canWrite}
          value={customerNotes}
          onChange={(event) => {
            const next = event.target.value;
            customerNotesRef.current = next;
            setCustomerNotes(next);
            notesDirtyRef.current = true;
            setNotesDirty(true);
            noteRevision.current += 1;
            draftRevision.current += 1;
            requiresPreflight.current = true;
            schedulePersist();
          }}
        />
        {canWrite ? (
          <div className="mt-2">
            <Button
              variant="primary"
              busy={sync.isPending && sync.variables === 'notes'}
              disabled={(conflict !== 'none' && conflict !== 'completed-lines') || complete.isPending}
              onClick={() => requestSync('notes')}
            >
              {t('inspections.saveNotes')}
            </Button>
          </div>
        ) : null}
      </section>

      {/*
        Publishing is an account decision, not a field one: a supervisor scores
        a building, and whether a facilities manager gets to read that
        scorecard is the office's call. The API refuses it without
        `customers:write`, so the control is hidden rather than left to fail.
      */}
      {can('customers:write') ? (
        <section className="card mt-6">
          <h2 className="mb-4 text-lg font-semibold">{t('inspections.sharingTitle')}</h2>
          <Checkbox
            label={t('inspections.sharedWithClient')}
            hint={completed ? t('inspections.sharedWithClientHint') : t('inspections.sharedBeforeCompleteHint')}
            checked={data.sharedWithClient}
            disabled={share.isPending || !isOnline || conflict !== 'none'}
            onChange={(event) => {
              if (isOnline) share.mutate(event.target.checked);
            }}
          />
          {share.isError ? (
            <Alert tone="error" title={t('inspections.shareErrorTitle')}>
              {(share.error as Error).message}
            </Alert>
          ) : null}
        </section>
      ) : null}

      <CorrectiveActions inspection={data} items={items} canWrite={canWrite} isOnline={isOnline} />

      {syncError ? (
        <Alert tone="error" title={t('inspections.saveErrorTitle')}>
          {syncError}
        </Alert>
      ) : null}

      {confirmingComplete ? (
        <ConfirmDialog
          title={t('inspections.completeConfirmTitle')}
          confirmLabel={t('inspections.complete')}
          busy={complete.isPending}
          error={complete.isError ? (complete.error as Error).message : null}
          onCancel={() => setConfirmingComplete(false)}
          onConfirm={() => {
            if (isOnline) complete.mutate();
          }}
        >
          {!isOnline ? (
            <Alert tone="warning" title="Reconnect to complete">
              Completion is intentionally online-only because it freezes the server score.
            </Alert>
          ) : null}
          {/*
            The unsaved-lines warning matters more here than anywhere else on
            the page: completing scores what the SERVER holds, so a line typed
            and not saved is a line that does not count towards the number the
            client is about to be shown.
          */}
          {linesDirty ? (
            <Alert tone="warning" title={t('inspections.completeUnsavedTitle')}>
              {t('inspections.completeUnsavedBody')}
            </Alert>
          ) : null}
          <p>{t('inspections.completeConfirmBody')}</p>
        </ConfirmDialog>
      ) : null}
    </WorkPage>
  );
}

function ScoredLines({
  inspectionId,
  propertyId,
  scheduledFor,
  items,
  editable,
  uploadsOnline,
  onEdit,
  onAdd,
  onAddMany,
  onRemove,
  onUploadStateChange,
}: {
  inspectionId: string;
  propertyId: string;
  scheduledFor: string;
  items: DraftItem[];
  editable: boolean;
  uploadsOnline: boolean;
  onEdit: (key: string, patch: Partial<DraftItem>) => void;
  onAdd: () => void;
  onAddMany: (lines: readonly InspectionLineSeed[]) => void;
  onRemove: (key: string) => void;
  onUploadStateChange: (delta: 1 | -1) => void;
}) {
  const { t } = useTranslation();

  const ratingOptions = [
    { value: '', label: t('inspections.ratingNotChecked') },
    ...Array.from({ length: INSPECTION_RATING_MAX - INSPECTION_RATING_MIN + 1 }, (_, index) => {
      const rating = INSPECTION_RATING_MIN + index;
      return {
        value: String(rating),
        label: t(`inspections.ratingScale.${rating}`),
      };
    }),
  ];

  return (
    <section className="card mt-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t('inspections.linesTitle')}</h2>
        {editable ? <Button onClick={onAdd}>{t('inspections.addLine')}</Button> : null}
      </div>

      {items.length === 0 ? (
        <EmptyState title={t('inspections.noLinesTitle')}>
          <p>{editable ? t('inspections.noLinesBody') : t('inspections.noLinesFrozenBody')}</p>
        </EmptyState>
      ) : null}
      {items.length === 0 && editable ? (
        <FillFromList propertyId={propertyId} scheduledFor={scheduledFor} onFill={onAddMany} />
      ) : null}

      <ul className="divide-y divide-[color:var(--color-line)]">
        {items.map((item) => (
          <li key={item.key} className="py-4">
            <div className="grid gap-x-4 sm:grid-cols-3">
              <TextInput
                label={t('inspections.areaName')}
                value={item.areaName}
                disabled={!editable}
                onChange={(event) => onEdit(item.key, { areaName: event.target.value })}
              />
              <TextInput
                label={t('inspections.itemName')}
                value={item.itemName}
                disabled={!editable}
                onChange={(event) => onEdit(item.key, { itemName: event.target.value })}
              />
              <Select
                label={t('inspections.rating')}
                value={item.rating === null || item.rating === undefined ? '' : String(item.rating)}
                disabled={!editable}
                options={ratingOptions}
                onChange={(event) =>
                  onEdit(item.key, {
                    rating: event.target.value === '' ? null : Number(event.target.value),
                  })
                }
              />
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-0 flex-1 sm:min-w-64">
                <TextInput
                  label={t('inspections.comment')}
                  value={item.comment ?? ''}
                  disabled={!editable}
                  onChange={(event) => onEdit(item.key, { comment: event.target.value })}
                />
              </div>
              <div className="mb-4">
                <LinePhoto
                  inspectionId={inspectionId}
                  fileId={item.photoFileId ?? null}
                  editable={editable && uploadsOnline}
                  onChange={(photoFileId) => onEdit(item.key, { photoFileId })}
                  onUploadStateChange={onUploadStateChange}
                />
              </div>
              {editable ? (
                <div className="mb-4">
                  <Button onClick={() => onRemove(item.key)}>{t('inspections.removeLine')}</Button>
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * An empty walkthrough filled from a list in one step — the building's
 * checklist, a template or the standard walkthrough — rather than typed line
 * by line on the walk. For one scheduled before there was a list to start
 * from, or scheduled blank on purpose.
 */
function FillFromList({
  propertyId,
  scheduledFor,
  onFill,
}: {
  propertyId: string;
  scheduledFor: string;
  onFill: (lines: readonly InspectionLineSeed[]) => void;
}) {
  const { t } = useTranslation();
  const [source, setSource] = useState<InspectionLineSource | null>(null);
  const sources = useInspectionLineSources(propertyId, scheduledFor);
  const chosen = chooseLineSource(sources, source);

  return (
    <div className="mt-4 flex flex-wrap items-start gap-3">
      <div className="min-w-0 flex-1 sm:min-w-64">
        <InspectionLineSourceField sources={sources} chosen={chosen} onChange={setSource} includeBlank={false} />
      </div>
      <div className="sm:mt-7">
        <Button
          disabled={chosen.lines.length === 0 || (sources.loading && source === null)}
          onClick={() => onFill(chosen.lines)}
        >
          {t('inspections.lineSource.apply')}
        </Button>
      </div>
    </div>
  );
}

/**
 * The promises made about the bad lines.
 *
 * Their own section, and their own lifetime: an action outlives the line that
 * prompted it, and stays raisable after the walkthrough is frozen — a
 * completed inspection is the most likely moment for somebody to read a 2 and
 * decide who is fixing it.
 */
function CorrectiveActions({
  inspection,
  items,
  canWrite,
  isOnline,
}: {
  inspection: Inspection;
  items: DraftItem[];
  canWrite: boolean;
  isOnline: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [description, setDescription] = useState('');
  const [dueOn, setDueOn] = useState('');
  const [itemId, setItemId] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');

  /*
    Who owns the fix. The stored action has carried an assignee since 0092 and
    the office screen was leaving it null on every action it raised — so the
    cross-walkthrough work list could say what was promised and by when, but
    never by whom, which is the first question asked of an overdue one.
  */
  const team = useQuery({
    queryKey: ['company-users'],
    queryFn: () => companyApi.users(),
  });
  const assignees = (team.data?.items ?? []).filter((member) => member.isActive);
  /*
    Null when the name is genuinely unknown, undefined when the roster never
    arrived. The difference matters: treating a failed fetch as an empty
    roster labeled every assigned action as belonging to somebody who had
    left, which is a claim about a colleague rather than a loading state.
  */
  const nameFor = (userId: string | null): string | null | undefined =>
    team.isError || !team.data ? undefined : (assignees.find((member) => member.id === userId)?.fullName ?? null);
  const [resolving, setResolving] = useState<string | null>(null);
  const [note, setNote] = useState('');

  /**
   * Opens the note field against one action, closing whichever was open.
   *
   * Clearing on every switch is the point: one shared field that kept its
   * text would file the explanation typed for one action against the next
   * one somebody opened — a wrong record rather than a wrong keystroke.
   */
  function openFor(actionId: string) {
    if (!isOnline) return;
    setResolving((current) => (current === actionId ? null : actionId));
    setNote('');
  }

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: ['inspection', inspection.id],
    });
    void queryClient.invalidateQueries({ queryKey: ['inspection-actions'] });
  };

  const raise = useMutation({
    mutationFn: () => {
      if (!isOnline) throw new Error('Reconnect before raising a corrective action.');
      return inspectionApi.raiseAction(inspection.id, {
        description,
        dueOn: dueOn || null,
        inspectionItemId: itemId || null,
        assignedUserId: assignedUserId || null,
      });
    },
    onSuccess: () => {
      setDescription('');
      setDueOn('');
      setItemId('');
      setAssignedUserId('');
      invalidate();
    },
  });

  const resolve = useMutation({
    mutationFn: (actionId: string) => {
      if (!isOnline) throw new Error('Reconnect before resolving a corrective action.');
      return inspectionApi.resolveAction(actionId, {
        resolutionNote: note || null,
      });
    },
    onSuccess: () => {
      setResolving(null);
      setNote('');
      invalidate();
    },
  });

  /*
    Only saved lines can be pointed at — an unsaved line has no id yet, and the
    server refuses an action against a line that is not on this inspection.
  */
  const linkable = items.filter((item): item is DraftItem & { id: string } => Boolean(item.id));

  return (
    <section className="card mt-6">
      <h2 className="mb-1 text-lg font-semibold">{t('inspections.actionsTitle')}</h2>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('inspections.actionsBody')}</p>

      {inspection.actions.length === 0 ? (
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('inspections.noActions')}</p>
      ) : (
        <ul className="mb-4 divide-y divide-[color:var(--color-line)]">
          {inspection.actions.map((action) => (
            <li key={action.id} className="py-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1 sm:min-w-56">
                  <p>{action.description}</p>
                  <p className="text-sm text-[color:var(--color-muted)]">
                    <ActionState action={action} />
                    {action.assignedUserId ? (
                      <>
                        {' · '}
                        {t('inspections.actionAssignedTo', {
                          name:
                            nameFor(action.assignedUserId) ??
                            (team.isError || !team.data
                              ? t('inspections.actionAssigneeUnknown')
                              : t('inspections.actionFormerMember')),
                        })}
                      </>
                    ) : null}
                  </p>
                  {action.resolutionNote ? <p className="text-sm">{action.resolutionNote}</p> : null}
                </div>
                {action.resolvedAt === null && canWrite ? (
                  <Button disabled={!isOnline} onClick={() => openFor(action.id)}>
                    {t('inspections.resolveAction')}
                  </Button>
                ) : null}
              </div>

              {resolving === action.id ? (
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <div className="min-w-0 flex-1 sm:min-w-64">
                    <TextInput
                      label={t('inspections.resolutionNote')}
                      value={note}
                      disabled={!isOnline}
                      onChange={(event) => setNote(event.target.value)}
                    />
                  </div>
                  <div className="mb-4">
                    <Button
                      variant="primary"
                      busy={resolve.isPending}
                      disabled={!isOnline}
                      onClick={() => resolve.mutate(action.id)}
                    >
                      {t('inspections.confirmResolve')}
                    </Button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canWrite ? (
        <>
          <div className="grid gap-x-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <TextInput
                label={t('inspections.actionDescription')}
                value={description}
                disabled={!isOnline}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
            <TextInput
              label={t('inspections.actionDueOn')}
              type="date"
              value={dueOn}
              disabled={!isOnline}
              onChange={(event) => setDueOn(event.target.value)}
            />
          </div>
          {/*
            An empty picker with no explanation reads as "this company has no
            active members", so a failed roster says so instead.
          */}
          {team.isError ? (
            <Alert tone="error" title={t('inspections.teamErrorTitle')}>
              {(team.error as Error).message}
            </Alert>
          ) : (
            <Select
              label={t('inspections.actionAssignee')}
              value={assignedUserId}
              disabled={!isOnline}
              onChange={(event) => setAssignedUserId(event.target.value)}
              options={[
                { value: '', label: t('inspections.actionUnassigned') },
                ...assignees.map((member) => ({
                  value: member.id,
                  label: member.fullName || member.email,
                })),
              ]}
            />
          )}
          {linkable.length > 0 ? (
            <Select
              label={t('inspections.actionLine')}
              value={itemId}
              disabled={!isOnline}
              onChange={(event) => setItemId(event.target.value)}
              options={[
                { value: '', label: t('inspections.actionLineNone') },
                ...linkable.map((item) => ({
                  value: item.id,
                  label: `${item.areaName} · ${item.itemName}`,
                })),
              ]}
            />
          ) : null}

          {raise.isError ? (
            <Alert tone="error" title={t('inspections.raiseErrorTitle')}>
              {(raise.error as Error).message}
            </Alert>
          ) : null}
          {resolve.isError ? (
            <Alert tone="error" title={t('inspections.resolveErrorTitle')}>
              {(resolve.error as Error).message}
            </Alert>
          ) : null}

          <Button
            variant="primary"
            busy={raise.isPending}
            disabled={!isOnline || !description.trim()}
            onClick={() => raise.mutate()}
          >
            {t('inspections.raiseAction')}
          </Button>
        </>
      ) : null}
    </section>
  );
}

function ActionState({ action }: { action: InspectionAction }) {
  const { t } = useTranslation();
  if (action.resolvedAt) {
    return (
      <>
        {t('inspections.actionResolvedOn', {
          date: formatShortDate(action.resolvedAt.slice(0, 10)),
        })}
      </>
    );
  }
  if (action.dueOn) return <>{t('inspections.actionDueBy', { date: formatShortDate(action.dueOn) })}</>;
  return <>{t('inspections.actionOpenNoDate')}</>;
}

/**
 * The photograph on one scored line.
 *
 * Uploading and attaching are two steps, and the seam between them is
 * deliberate: the upload lands the file against the WALKTHROUGH immediately,
 * and the line only holds its id until the next save. So an upload is never
 * lost because the save after it failed, a photo moved between lines costs no
 * second upload, and a supervisor on a bad connection in a stairwell can get
 * the evidence off their phone before they finish scoring.
 *
 * Which also means the pointer is only durable once the lines are saved — the
 * hint under the control says so rather than letting somebody assume
 * otherwise.
 */
function LinePhoto({
  inspectionId,
  fileId,
  editable,
  onChange,
  onUploadStateChange,
}: {
  inspectionId: string;
  fileId: string | null;
  editable: boolean;
  onChange: (fileId: string | null) => void;
  onUploadStateChange: (delta: 1 | -1) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputId = useId();

  const upload = useMutation({
    mutationFn: (file: File) => fileApi.uploadInspectionPhoto(inspectionId, file),
    // Counted while in flight so the page can refuse to complete the
    // walkthrough underneath an upload that is still traveling.
    onMutate: () => onUploadStateChange(1),
    onSettled: () => onUploadStateChange(-1),
    onSuccess: (uploaded) => {
      onChange(uploaded.id);
      // The recovery list has to know about it, in case the save that would
      // attach it never lands.
      void queryClient.invalidateQueries({ queryKey: ['inspection-photos'] });
    },
  });

  /*
    Deletes the object as well as detaching it. The alternative — detaching
    only — leaves a paid-for photograph on the walkthrough that no line shows
    and nobody can reach, which is the sort of thing that quietly fills a
    bucket. The server clears any line still pointing at it.
  */
  const remove = useMutation({
    mutationFn: (id: string) => fileApi.remove(id),
    // Removal changes the same evidence completion freezes. Count it with an
    // upload so the parent disables completion until the server has either
    // acknowledged the delete or left the pointer untouched on failure.
    onMutate: () => onUploadStateChange(1),
    onSettled: () => onUploadStateChange(-1),
    onSuccess: () => {
      onChange(null);
      void queryClient.invalidateQueries({ queryKey: ['inspection-photos'] });
    },
  });

  if (fileId) {
    return (
      <div className="flex items-end gap-2">
        <PhotoTrigger src={`${API_BASE}/files/${fileId}`} alt={t('inspections.photoAlt')} group="inspection-photos">
          <img
            src={`${API_BASE}/files/${fileId}`}
            alt={t('inspections.photoAlt')}
            className="h-16 w-16 rounded border border-[color:var(--color-line)] object-cover"
          />
        </PhotoTrigger>
        {editable ? (
          <div>
            <Button
              busy={remove.isPending}
              onClick={() => {
                if (window.confirm(t('inspections.removePhotoConfirm'))) remove.mutate(fileId);
              }}
            >
              {t('inspections.removePhoto')}
            </Button>
            {/*
              Here rather than only in the no-photo branch below: a failed
              removal leaves the photo attached, so that branch never renders
              and every failure was silent.
            */}
            {remove.isError ? (
              <Alert tone="error" title={t('inspections.photoRemoveErrorTitle')}>
                {(remove.error as Error).message}
              </Alert>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  if (!editable) return null;

  return (
    <div>
      <label className="field-label" htmlFor={inputId}>
        {t('inspections.photo')}
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        // `capture` is what turns this into "take a photo" on a phone, which
        // is where a walkthrough is actually scored.
        capture="environment"
        disabled={upload.isPending}
        className="field-control"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so re-picking the same file fires a change event again.
          event.target.value = '';
          if (file) upload.mutate(file);
        }}
      />
      <p className="field-hint">{t('inspections.photoHint')}</p>
      {upload.isError ? (
        <Alert tone="error" title={t('inspections.photoErrorTitle')}>
          {(upload.error as Error).message}
        </Alert>
      ) : null}
      {remove.isError ? (
        <Alert tone="error" title={t('inspections.photoRemoveErrorTitle')}>
          {(remove.error as Error).message}
        </Alert>
      ) : null}
    </div>
  );
}

/**
 * Photographs on this walkthrough that no line is showing.
 *
 * The upload lands against the walkthrough immediately and the line only holds
 * its id until the next save — so a save that fails, a closed tab, or a phone
 * that lost signal in a stairwell leaves a photograph stored, paid for, and
 * invisible. It is also the piece of evidence somebody went and took.
 *
 * So the orphans are shown rather than hidden: attach one to a line, or delete
 * it. Ordinarily this renders nothing at all, which is the point — it appears
 * exactly when something went wrong.
 */
function UnattachedPhotos({
  photos,
  items,
  editable,
  onAttach,
}: {
  photos: Array<{ id: string; createdAt: string }>;
  items: DraftItem[];
  editable: boolean;
  onAttach: (fileId: string, lineKey: string) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<Record<string, string>>({});

  const remove = useMutation({
    mutationFn: (id: string) => fileApi.remove(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['inspection-photos'] }),
  });

  const attached = new Set(items.map((item) => item.photoFileId).filter(Boolean));
  const orphans = photos.filter((photo) => !attached.has(photo.id));
  if (orphans.length === 0) return null;

  return (
    <section className="card mt-6">
      <h2 className="mb-1 text-lg font-semibold">{t('inspections.strayPhotosTitle')}</h2>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('inspections.strayPhotosBody')}</p>

      <ul className="flex flex-wrap gap-4">
        {orphans.map((photo) => (
          <li key={photo.id} className="w-48">
            <PhotoTrigger
              src={`${API_BASE}/files/${photo.id}`}
              alt={t('inspections.photoAlt')}
              group="inspection-photos"
            >
              <img
                src={`${API_BASE}/files/${photo.id}`}
                alt={t('inspections.photoAlt')}
                className="h-32 w-full rounded border border-[color:var(--color-line)] object-cover"
              />
            </PhotoTrigger>
            {editable ? (
              <>
                <Select
                  label={t('inspections.strayPhotoAttach')}
                  value={target[photo.id] ?? ''}
                  options={[
                    { value: '', label: t('inspections.strayPhotoChooseLine') },
                    ...items.map((item) => ({
                      value: item.key,
                      label: `${item.areaName || t('inspections.areaName')} · ${item.itemName || '—'}`,
                    })),
                  ]}
                  onChange={(event) => {
                    const key = event.target.value;
                    setTarget((current) => ({ ...current, [photo.id]: key }));
                    if (key) onAttach(photo.id, key);
                  }}
                />
                <Button
                  busy={remove.isPending}
                  onClick={() => {
                    if (window.confirm(t('inspections.removePhotoConfirm'))) remove.mutate(photo.id);
                  }}
                >
                  {t('inspections.removePhoto')}
                </Button>
              </>
            ) : null}
          </li>
        ))}
      </ul>

      {remove.isError ? (
        <Alert tone="error" title={t('inspections.photoRemoveErrorTitle')}>
          {(remove.error as Error).message}
        </Alert>
      ) : null}
    </section>
  );
}
